import _ from 'underscore';
import Backbone from "backbone";
import Chaplin from "chaplin";
import WorkspaceMembersPopupView from "views/workspace/WorkspaceMembersPopupView";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import LocalStorageHelper from "models/helper/LocalStorageHelper";
import Utils from "models/helper/Utils";
import ti18n from "i18n!nls/WorkspaceMembersi18n";

const WorkspaceMembersPopupController = Chaplin.Controller.extend({

    initialize: function(options) {
        _.extend(this, Backbone.Events);
        this._parentController = options.parentController;
        this._workspaceId = options.workspaceId || null;
        this._mode = options.mode === "invite" ? "invite" : "manage";
        this._allowInviteModeBypass = options.allowInviteModeBypass === true;
        this._onInviteSuccess = typeof options.onInviteSuccess === "function" ? options.onInviteSuccess : null;
        this._members = [];
        this._seatLimit = null;
        this._seatUsed = 0;
    },

    getModel: function() {
        return this.model;
    },

    getView: function() {
        return this.view;
    },

    onPopupClosed: function() {
        // Cleanup if needed
    },

    _getWorkspaceId: function() {
        return this._workspaceId || LocalStorageHelper.getInstance().getCurrentWorkspaceId();
    },

    _deriveIsOwnerOrAdmin: function() {
        // Derive from the live member list first — this reflects the current state
        // after any role promotions/demotions that happened during this session.
        //
        // Member rows come straight from the backend and have historically used
        // different id fields on each side (userInfo._id vs member.userUID vs
        // member.userId vs member._id = membership doc id). Match across every
        // plausible field so an owner never falls through to the fallback just
        // because one end labels the id differently.
        var currentUserInfo = LocalStorageHelper.getInstance().getUserInfo();
        var currentUserCandidates = [];
        if (currentUserInfo) {
            ['_id', 'userUID', 'userId', 'id'].forEach(function(field) {
                if (currentUserInfo[field]) {
                    currentUserCandidates.push(currentUserInfo[field]);
                }
            });
        }
        var memberIdOf = function(m) {
            return m.userUID || m.userId || m._id || null;
        };
        if (currentUserCandidates.length > 0) {
            var currentMember = this._members.find(function(m) {
                var memberUserId = memberIdOf(m);
                return memberUserId && currentUserCandidates.indexOf(memberUserId) !== -1;
            });
            if (currentMember) {
                return currentMember.role === 'owner' ||
                    currentMember.role === 'admin' ||
                    currentMember.isAdmin === true;
            }
        }
        // When we can't confidently match the current user in the member list,
        // the "invite" flow has already passed through the canManageMembers gate
        // in SettingsPage.js — trust that gate so the invite form isn't hidden
        // by a field-name mismatch. In "manage" mode we're conservative and
        // fall back to the cached workspace context, which otherwise governs
        // whether management controls appear.
        if (this._mode === 'invite' && this._allowInviteModeBypass) {
            return true;
        }
        return LocalStorageHelper.getInstance().isCurrentUserOwnerOrAdminInWorkspace();
    },

    _fetchMembers: function() {
        var workspaceId = this._getWorkspaceId();
        return TrebbleClientAPIHelper.getInstance().listWorkspaceMembers({
            workspaceId: workspaceId
        }).then((function(result) {
            var hasSeatUsed = false;
            if (result && Array.isArray(result)) {
                this._members = result;
            } else if (result && result.members && Array.isArray(result.members)) {
                this._members = result.members;
                // Some API responses include seat limit info alongside members
                if (result.seatLimit != null) {
                    this._seatLimit = result.seatLimit;
                }
                if (result.seatUsed != null) {
                    this._seatUsed = result.seatUsed;
                    hasSeatUsed = true;
                }
            } else {
                this._members = [];
            }
            // Derive seat usage from active members when not provided by the API
            if (!hasSeatUsed) {
                // Count active editor/admin/owner seats (non-pending)
                this._seatUsed = this._members.filter(function(m) {
                    return m.status !== 'invited' && m.status !== 'pending' &&
                        (m.role === 'editor' || m.role === 'admin' || m.role === 'owner');
                }).length;
            }
            return this._members;
        }).bind(this));
    },

    _refreshMemberList: function() {
        return this._fetchMembers().then((function() {
            if (this.view) {
                this.view.updateMembers(this._members, this._seatLimit, this._seatUsed);
            }
        }).bind(this)).catch(function(error) {
            console.error('Failed to refresh member list:', error);
        });
    },

    _runInviteSuccessCallback: async function(response) {
        if (!this._onInviteSuccess) {
            return;
        }
        try {
            // Forward the raw invite response so the parent can react to
            // server-side workspace materialization
            // (`response.workspaceMaterialized`, `response.workspaceId`).
            await this._onInviteSuccess(response);
        } catch (error) {
            console.error('Failed to run invite success callback:', error);
        }
    },

    _buildViewParams: function(isOwnerOrAdmin, members) {
        return {
            members: members,
            isOwnerOrAdmin: isOwnerOrAdmin,
            seatLimit: this._seatLimit,
            seatUsed: this._seatUsed,
            onInvite: this.handleInvite.bind(this),
            onRemove: this.handleRemove.bind(this),
            onSetRole: this.handleSetRole.bind(this),
            onSetAdmin: this.handleSetAdmin.bind(this),
            i18nModule: ti18n,
            mode: this._mode
        };
    },

    handleInvite: async function(email, role) {
        try {
            var inviteRole = role;
            if (inviteRole !== 'editor') {
                inviteRole = 'editor';
            }
            // Send through whatever workspaceId the popup was opened with —
            // for legacy creator-sub users on the synthesized default
            // workspace this is `null`, and the backend materializes a
            // Workspace doc on the fly. The response carries the (possibly
            // new) workspaceId + a `workspaceMaterialized` flag that lets the
            // parent controller show a "Workspace created" toast.
            var workspaceId = this._getWorkspaceId();
            var response = await TrebbleClientAPIHelper.getInstance().inviteWorkspaceMember({
                workspaceId: workspaceId,
                email: email,
                role: inviteRole
            });
            // When the backend just materialized the workspace, the parent
            // surfaces a more specific "Workspace created — invitation sent"
            // toast. Skip the generic success here so we don't double-toast.
            if (!(response && response.workspaceMaterialized)) {
                window.alertSuccessMessage(window.getI18n(ti18n, "INVITE_SUCCESS"));
            }
            if (this._mode === "invite") {
                if (this.view) {
                    this.view.close();
                }
                this._runInviteSuccessCallback(response);
                return;
            }
            // Refresh members + seat counts from the server so the client
            // cannot hold a stale seatUsed/seatLimit after a successful invite.
            await this._refreshMemberList();
            await this._runInviteSuccessCallback(response);
        } catch (error) {
            console.error('Workspace member invite error:', error);
            // Let the React component render a specific, server-aware error
            // message based on the error code. A second global alert here
            // would be redundant and squelch the code-specific copy.
            throw error;
        }
    },

    handleRemove: async function(memberRecordId) {
        const confirmTitle = window.getI18n(ti18n, "REMOVE_CONFIRM_TITLE");
        const confirmBody = window.getI18n(ti18n, "REMOVE_CONFIRM_BODY") + "\n\n" + window.getI18n(ti18n, "PROJECTS_TRANSFER_NOTE");

        let confirmed = false;
        try {
            confirmed = await Utils.getInstance().showUserConfirmationDialog(confirmTitle, confirmBody);
        } catch (e) {
            console.warn('Confirmation dialog error, treating as cancellation:', e);
            return;
        }
        if (confirmed !== true) return;

        try {
            const workspaceId = this._getWorkspaceId();
            await TrebbleClientAPIHelper.getInstance().removeWorkspaceMember({
                workspaceId: workspaceId,
                memberRecordId: memberRecordId
            });
            window.alertSuccessMessage(window.getI18n(ti18n, "REMOVE_SUCCESS"));
            // Seat counts decrease on remove — re-fetch authoritative values.
            await this._refreshMemberList();
        } catch (error) {
            console.error('Workspace member remove error:', error);
            throw error;
        }
    },

    handleSetRole: async function(memberRecordId, newRole) {
        try {
            var workspaceId = this._getWorkspaceId();
            var roleForPayload = newRole === 'admin' ? 'editor' : newRole;
            await TrebbleClientAPIHelper.getInstance().setWorkspaceMemberRole({
                workspaceId: workspaceId,
                memberRecordId: memberRecordId,
                role: roleForPayload
            });
            if (newRole === 'admin') {
                await TrebbleClientAPIHelper.getInstance().setWorkspaceMemberAdmin({
                    workspaceId: workspaceId,
                    memberRecordId: memberRecordId,
                    isAdmin: true
                });
            }
            window.alertSuccessMessage(window.getI18n(ti18n, "ROLE_CHANGED"));
            // Role promotions/demotions can change seat usage (e.g. viewer ->
            // editor consumes a paid seat). Re-fetch rather than trust
            // client-side accounting.
            await this._refreshMemberList();
        } catch (error) {
            console.error('Workspace member set role error:', error);
            throw error;
        }
    },

    handleSetAdmin: async function(memberRecordId, isAdmin) {
        try {
            var workspaceId = this._getWorkspaceId();
            await TrebbleClientAPIHelper.getInstance().setWorkspaceMemberAdmin({
                workspaceId: workspaceId,
                memberRecordId: memberRecordId,
                isAdmin: isAdmin
            });
            window.alertSuccessMessage(window.getI18n(ti18n, "ADMIN_TOGGLED"));
            await this._refreshMemberList();
        } catch (error) {
            console.error('Workspace member set admin error:', error);
            throw error;
        }
    },

    buildModelAndRenderView: function() {
        return this._fetchMembers().then((function() {
            var isOwnerOrAdmin = this._deriveIsOwnerOrAdmin();
            var params = this._buildViewParams(isOwnerOrAdmin, this._members);
            this.view = new WorkspaceMembersPopupView(params);
            this.listenTo(this.view, "closed", this.onPopupClosed);

            this.view.render();
        }).bind(this)).catch((function(error) {
            // If loading members fails, show empty state
            console.error('Failed to load workspace members:', error);

            var isOwnerOrAdmin = this._deriveIsOwnerOrAdmin();
            var params = this._buildViewParams(isOwnerOrAdmin, []);
            this.view = new WorkspaceMembersPopupView(params);
            this.listenTo(this.view, "closed", this.onPopupClosed);

            this.view.render();
        }).bind(this));
    }
});

export default WorkspaceMembersPopupController;
